/**
 * What changed in the ledger since an earlier run of the same period.
 *
 * A reviewed workpaper leaves two questions open: were the findings actually
 * fixed, and what else moved that nobody asked for. The second is the one
 * nobody checks by hand -- an invoice posted since the review, a credit note
 * voided, an unrelated entry edited -- and a workpaper showing only the
 * findings resolved hides all of it.
 *
 * A page rather than a sheet in the workbook, because *which* earlier run to
 * compare against is a choice. Baked into a workpaper it could only ever mean
 * "whatever ran before me", and regenerating twice then reports a quarter in
 * which nothing happened.
 */
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { getWorkpaperDiff } from "../lib/api";
import type { DiffOption, LineChange, WorkpaperDiff as Diff } from "../lib/api";
import { formatWhen } from "../lib/format";

/** Why a comparison could not be made, in words a preparer can act on. */
const REASONS: Record<string, string> = {
  no_earlier_run:
    "This is the first run of this period, so there is nothing earlier to compare it with.",
  no_baseline:
    "That run was generated before this feature existed, so it did not store the ledger it saw. " +
    "Its workpaper is unaffected. Generate this period again to create a comparison point.",
  unreadable_baseline:
    "That run's stored ledger could not be read, so a comparison would be guesswork.",
};

const KINDS: Record<LineChange["kind"], string> = {
  recoded: "Recoded",
  amount: "GST changed",
  redated: "Date moved",
  added: "Added",
  removed: "Removed",
};

/** What a change did to the line, in the terms the ledger uses. */
function movement(change: LineChange): string {
  switch (change.kind) {
    case "recoded":
      return `${change.from_code} → ${change.to_code}`;
    case "added":
      return `posted since, coded ${change.to_code}`;
    case "removed":
      return `was coded ${change.from_code}`;
    default:
      return change.from_code;
  }
}

function optionLabel(option: DiffOption): string {
  const when = formatWhen(option.created_at);
  const reviewed = option.reviewed
    ? ` · reviewed, ${option.findings} finding${option.findings === 1 ? "" : "s"}`
    : "";
  // Said in the option itself, so choosing one that cannot work is not a
  // click that ends in an explanation.
  const unavailable = option.diffable ? "" : " · no stored ledger";
  return `${when}${reviewed}${unavailable}`;
}

export function WorkpaperDiff({ jobId }: { jobId: string }) {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [diff, setDiff] = useState<Diff | null>(null);
  //: Which earlier run the preparer chose. Empty means "whichever the API
  //: thinks is worth comparing against", which is the run they reviewed.
  const [chosen, setChosen] = useState<string>("");
  const [kind, setKind] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    void (async () => {
      try {
        const answer = await getWorkpaperDiff(token, jobId, chosen || undefined);
        if (!alive) return;
        setDiff(answer);
        setError(null);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Could not compare the runs");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, jobId, chosen]);

  // Shown rather than stored, so the picker always agrees with what is on
  // screen -- including the run the API picked when nothing was chosen.
  const showingAgainst = chosen || diff?.against?.job_id || "";

  if (loading && !diff) return <div className="muted">Comparing runs…</div>;
  if (error) return <div className="field-error">{error}</div>;
  if (!diff) return null;

  const picker = diff.options.length > 0 && (
    <label>
      <span className="muted">Compared with </span>
      <select
        aria-label="Earlier run to compare against"
        value={showingAgainst}
        onChange={(e) => {
          setLoading(true);
          setChosen(e.target.value);
        }}
      >
        {diff.options.map((option) => (
          <option key={option.job_id} value={option.job_id} disabled={!option.diffable}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );

  if (!diff.comparable) {
    return (
      <div className="diff">
        {picker}
        <p className="muted">{REASONS[diff.reason ?? ""] ?? "These runs cannot be compared."}</p>
      </div>
    );
  }

  const summary = diff.summary!;
  const changes = diff.changes ?? [];
  const shown = kind === "all" ? changes : changes.filter((c) => c.kind === kind);
  const moved = Number(summary.gst_moved);

  // Two identical lines on one document are genuinely interchangeable -- it is
  // why the comparison counts duplicates rather than keying on them -- so a
  // row's identity is its content plus which occurrence of that content it is.
  const seen = new Map<string, number>();
  const rows = shown.map((change) => {
    const base = `${change.kind}:${change.source_id}:${change.account}:${change.gst_delta}`;
    const occurrence = (seen.get(base) ?? 0) + 1;
    seen.set(base, occurrence);
    return { change, key: `${base}#${occurrence}` };
  });

  return (
    <div className="diff">
      {picker}
      {diff.against?.generated && (
        <div className="muted">
          that run saw {diff.against.lines?.toLocaleString()} lines; this one{" "}
          {diff.lines?.toLocaleString()}
        </div>
      )}

      <ul className="diff-summary">
        <li>
          <strong>{summary.unchanged.toLocaleString()}</strong> unchanged
        </li>
        {(
          [
            ["recoded", summary.recoded],
            ["amount", summary.amount],
            ["redated", summary.redated],
            ["added", summary.added],
            ["removed", summary.removed],
          ] as const
        )
          .filter(([, count]) => count > 0)
          .map(([name, count]) => (
            <li key={name}>
              <strong>{count}</strong> {KINDS[name as LineChange["kind"]].toLowerCase()}
            </li>
          ))}
      </ul>

      {/* The number that matters: this is exactly what 1B shifts by between
          the two runs, which is what makes the list below checkable rather
          than just interesting. */}
      <p className={moved === 0 ? "muted" : "tag"}>
        {moved === 0 ? "GST unchanged between the runs" : `1B moves by ${summary.gst_moved}`}
      </p>

      {changes.length === 0 ? (
        <p className="muted">
          Nothing that affects the BAS has changed. Contacts and references are not compared,
          so this does not mean nobody touched the ledger.
        </p>
      ) : (
        <>
          <label>
            <span className="muted">Show </span>
            <select
              aria-label="Filter changes by kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="all">everything ({changes.length})</option>
              {(Object.keys(KINDS) as LineChange["kind"][])
                .filter((k) => changes.some((c) => c.kind === k))
                .map((k) => (
                  <option key={k} value={k}>
                    {KINDS[k].toLowerCase()} ({changes.filter((c) => c.kind === k).length})
                  </option>
                ))}
            </select>
          </label>

          <table className="diff-changes">
            <thead>
              <tr>
                <th>Date</th>
                <th>Account</th>
                <th>Details</th>
                <th className="num">GST</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ change, key }) => (
                <tr key={key}>
                  <td>{change.date}</td>
                  <td>{change.account}</td>
                  <td>
                    {change.xero_url ? (
                      <a href={change.xero_url} target="_blank" rel="noreferrer">
                        {change.details || change.reference || "open in Xero"}
                      </a>
                    ) : (
                      change.details || change.reference || "—"
                    )}
                  </td>
                  <td className="num">{change.gst_delta}</td>
                  <td>
                    <span className="tag">{KINDS[change.kind]}</span> {movement(change)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {diff.truncated && (
            <p className="muted">
              Only the first {changes.length} changes are shown. That many means the two runs
              are probably not the same ledger.
            </p>
          )}
        </>
      )}
    </div>
  );
}
